import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'; // Import MSAL types
import { AxiosInstance } from 'axios';
import { config } from './Constants';
import { API_SCOPES } from '../Azure/azure-authentication-config';
import { clearCookieSessionCache, isEmbeddedContext, markCookieSessionRejected, stripCookieTransport, tryCookieFallback, tryCookieFirst, usedCookieTransport } from './auth/cookieSession';

let msalInstance: PublicClientApplication | null = null; // Declare msalInstance as nullable
const apiBaseURL = config.url.API_URL;

// Helper function to check if error requires interaction
const isInteractionRequiredError = (error: any): boolean => {
  return error instanceof InteractionRequiredAuthError ||
         error.errorCode === "interaction_required" ||
         error.errorCode === "consent_required" ||
         error.errorCode === "login_required" ||
         error.errorCode === "token_expired";
};

// Helper function to initiate authentication flow with interaction state checking
const initiateAuthFlow = async (scopes: string[]) => {
  if (!msalInstance) {
    throw new Error("MSAL instance not available for authentication");
  }
  
  try {
    // Check if there's already an interaction in progress
    const activeRequest = (msalInstance as any).browserStorage?.getInteractionInProgress();
    if (activeRequest) {
      console.info("Authentication interaction already in progress, waiting...");
      // Return a promise that rejects to indicate auth is needed but in progress
      throw new Error("Authentication in progress - please wait");
    }
    
    await msalInstance.loginRedirect({ scopes });
  } catch (error: any) {
    if (error.errorCode === "interaction_in_progress") {
      console.info("Authentication interaction already in progress");
      throw new Error("Authentication in progress - please wait");
    }
    console.error("Failed to initiate authentication flow:", error);
    throw new Error("Authentication flow initiation failed");
  }
};
export const initializeService = (msalApp: PublicClientApplication, instance: AxiosInstance) => {
  msalInstance = msalApp; // Assign the initialized msal instance

  instance.interceptors.request.use(
    async function (config) { // Made the function async
      // Embedded views (Openings) ride the host's cookie session on their own endpoints
      // rather than asking MSAL for a token the webview may not have. No-op elsewhere.
      const cookieFirstConfig = await tryCookieFirst(config);
      if (cookieFirstConfig) {
        return cookieFirstConfig;
      }

      if (!msalInstance) {
        throw new Error("MSAL instance not initialized for service.");
      }

      const activeAccount = msalInstance.getActiveAccount();
      const accounts = msalInstance.getAllAccounts();

      if (!activeAccount && accounts.length === 0) {
        // Handle unauthenticated state appropriately
        console.warn("No active MSAL account found. User is not authenticated.");

        // Prefer token auth; fall back to an existing cookie session on embedded sub-routes.
        const cookieConfig = await tryCookieFallback(config);
        if (cookieConfig) {
          console.info("No MSAL account; using cookie session for embedded request.");
          return cookieConfig;
        }

        // Check if authentication is already in progress at the app level
        try {
          const activeRequest = (msalInstance as any).browserStorage?.getInteractionInProgress();
          if (activeRequest) {
            console.info("Authentication already in progress at app level, skipping API call");
            return Promise.reject(new Error("Authentication in progress - API call will retry after login"));
          }
        } catch (checkError) {
          // Ignore errors checking interaction state
        }
        
        // Don't initiate auth flow here - let the app-level authentication handle it
        console.info("Deferring to app-level authentication flow");
        return Promise.reject(new Error("Authentication required - handled by app level"));
      }

      const accessTokenRequest = {
        scopes: API_SCOPES,
        account: activeAccount || accounts[0],
      };

      try {
        const accessTokenResponse = await msalInstance.acquireTokenSilent(accessTokenRequest);
        config.headers.Authorization = `Bearer ${accessTokenResponse.accessToken}`;
        return config;
      } catch (error: any) {
        console.error("Error acquiring token silently:", error);

        // Token acquisition failed; before failing, try an existing cookie session
        // (embedded sub-routes only). Prefer token, fall back to cookie.
        const cookieConfig = await tryCookieFallback(config);
        if (cookieConfig) {
          console.info("Silent token acquisition failed; using cookie session for embedded request.");
          return cookieConfig;
        }

        // Handle specific MSAL errors that require interaction
        if (isInteractionRequiredError(error)) {
          console.info("Interactive authentication required. Checking for ongoing interactions...");
          
          // Check if interaction is already in progress before attempting
          try {
            const activeRequest = (msalInstance as any).browserStorage?.getInteractionInProgress();
            if (activeRequest) {
              console.info("Authentication interaction already in progress, skipping token request");
              return Promise.reject(new Error("Authentication in progress - token will be acquired after login"));
            }
          } catch (checkError) {
            // Ignore errors checking interaction state
          }
          
          // Attempt interactive token acquisition only if no interaction is in progress
          try {
            const scopes = API_SCOPES;
            await initiateAuthFlow(scopes);
            // This will redirect, so the code below won't execute
            return Promise.reject(new Error("Interactive authentication required - redirecting"));
          } catch (redirectError: any) {
            if (redirectError.message?.includes("Authentication in progress")) {
              return Promise.reject(new Error("Authentication in progress - please wait"));
            }
            console.error("Failed to initiate interactive token acquisition:", redirectError);
            return Promise.reject(new Error("Token acquisition failed"));
          }
        }
        
        // For other errors, reject the promise to let the calling component handle it
        return Promise.reject(error);
      }
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      // Handle 401 Unauthorized responses
      if (error.response?.status === 401) {
        console.warn("Received 401 Unauthorized response. Attempting to refresh authentication...");

        // Invalidate any cached cookie-session result so the next request re-verifies
        // instead of trusting a stale "yes" (the host session may have expired).
        clearCookieSessionCache();

        // The request rode the host cookie and the server still said 401 (typically with
        // `WWW-Authenticate: Bearer`), so the session probe's 2xx was not proof after all.
        // Stop trusting the cookie and retry this request once on the normal token path.
        if (usedCookieTransport(error.config)) {
          console.warn("Cookie session rejected with 401. Falling back to token auth.");
          markCookieSessionRejected();
          return instance.request(stripCookieTransport(error.config));
        }

        // On embedded sub-routes there is no MSAL session to refresh, and redirecting
        // to /logout would break the host webview — let the caller handle the 401.
        if (isEmbeddedContext()) {
          return Promise.reject(error);
        }

        if (msalInstance) {
          const activeAccount = msalInstance.getActiveAccount();
          const accounts = msalInstance.getAllAccounts();
          
          if (activeAccount || accounts.length > 0) {
            // Try to acquire a new token silently first
            try {
              const accessTokenRequest = {
                scopes: API_SCOPES,
                account: activeAccount || accounts[0],
              };
              
              const tokenResponse = await msalInstance.acquireTokenSilent(accessTokenRequest);
              // Retry the original request with the new token
              if (error.config && tokenResponse.accessToken) {
                error.config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
                return instance.request(error.config);
              }
            } catch (tokenError) {
              console.error("Failed to refresh token silently:", tokenError);
              
              // If silent refresh fails and it requires interaction, redirect to interactive login
              if (isInteractionRequiredError(tokenError)) {
                try {
                  const scopes = API_SCOPES;
                  await initiateAuthFlow(scopes);
                } catch (loginError) {
                  console.error("Failed to initiate login redirect:", loginError);
                  // Fallback: redirect to logout page
                  window.location.href = "/logout";
                }
              } else {
                // For other token errors, also fallback to logout
                console.error("Token refresh failed with non-interaction error:", tokenError);
                window.location.href = "/logout";
              }
            }
          } else {
            // No accounts available, redirect to login
            console.warn("No active account found for token refresh. Redirecting to login...");
            try {
              const scopes = API_SCOPES;
              await initiateAuthFlow(scopes);
            } catch (loginError) {
              console.error("Failed to initiate login redirect:", loginError);
              // Fallback: redirect to logout page
              window.location.href = "/logout";
            }
          }
        } else {
          // MSAL instance not available, fallback to logout
          console.error("MSAL instance not available for token refresh. Redirecting to logout...");
          window.location.href = "/logout";
        }
      }
      
      return Promise.reject(error);
    }
  );
};


const getWithAuth = (url: string, instance: AxiosInstance, config?: object) => {
  return instance.get(`${apiBaseURL}/${url}`, config);
}

const postWithAuth = (url: string, data: any, instance: AxiosInstance, config?: object) => {
  return instance.post(`${apiBaseURL}/${url}`, data, config);
}

const putWithAuth = (url: string, data: any, instance: AxiosInstance) => {
  return instance.put(`${apiBaseURL}/${url}`, data);
}

const deleteWithAuth = (url: string, instance: AxiosInstance) => {
  return instance.delete(`${apiBaseURL}/${url}`);
}

export { getWithAuth, postWithAuth, putWithAuth, deleteWithAuth };
